<template>
    <Modal ref="emailModal" :title="modalTitle" size="is-xxxlarge" :loading="isLoading" paddingless @closed="reset">
        <div class="tw-p-3 tw-grid tw-gap-3">
            <FormField label="Email Addresses">
                <Textbox
                    v-model="emailRecipient"
                    placeholder="Type an email address and press enter to add"
                    @keyup.enter.native="addEmailRecipient()"
                />
            </FormField>
            <div class="tw-flex tw-gap-3 tw-flex-wrap">
                <Tag
                    v-for="recipient in emailRecipients"
                    :key="recipient"
                    type="primary"
                    class="tw-flex tw-gap-3 tw-items-center"
                >
                    <span>{{ recipient }}</span>
                    <Button type="is-link" class="tw-text-white" @click="removeRecipient(recipient)">
                        <i class="mdi mdi-close tw-text-sm"></i>
                    </Button>
                </Tag>
            </div>

            <Banner v-if="type === 'purchase order'" :closable="false" type="is-info">
                Any attachable documents linked to this purchase order will also be attached to the PDF sent in the
                email.
            </Banner>

            <FormField v-if="type === 'invoice' && availableAttachments.length" label="Attached Terms and Conditions">
                <Select v-model="selectedAttachment" placeholder="Select a Document" class="tw-w-full">
                    <option v-for="document in availableAttachments" :key="document.id" :value="document.id">
                        {{ document.description }}
                    </option>
                </Select>
            </FormField>

            <Editor v-model="emailContent" :without="['image']" />
        </div>

        <template #footer>
            <Button class="!tw-mr-auto" @click="close">Cancel</Button>
            <Button type="is-success" :disabled="!emailRecipients.length || isSending" @click="submit">
                Send {{ emailRecipients.length > 1 ? 'Emails' : 'Email' }}
            </Button>
        </template>
    </Modal>
</template>

<script setup>
import Modal from '@/js/components/Modal.vue';
import Editor from '@/js/components/Editor.vue';
import Button from '@/js/components/controls/Button.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Select from '@/js/components/controls/Select.vue';
import Tag from '@/js/components/Tag.vue';
import { isValidEmail, toast } from '@/js/utils.js';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { watch } from 'vue';
import { Eventbus } from '@/js/utils/eventbus.ts';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const isLoading = ref(false);
const isSending = ref(false);
const emailable = ref(null);
const emailModal = useTemplateRef('emailModal');
const emailRecipient = ref('');
const emailRecipients = ref([]);
const emailContent = ref('');
const emailFile = ref(null);
const type = ref('');
const availableAttachments = ref([]);
const selectedAttachment = ref(null);
const modalTitle = computed(() => {
    if (!emailable.value?.reference) {
        return `Send ${type.value} by email`;
    }

    return `Send ${type.value} (${emailable.value?.reference}) by email`;
});
watch(emailable, (val) => {
    if (val) {
        getMailTemplate();
    }
});
onMounted(() => {
    Eventbus.$on('send:email', (data) => {
        emailable.value = data.model;
        emailFile.value = data.file ?? null;
        type.value = data.type;
        emailModal.value.open();
    });
});
const close = () => {
    reset();
    emailModal.value.close();
};
const reset = () => {
    emailRecipients.value = [];
    emailContent.value = '';
    emailFile.value = null;
    emailable.value = null;
    isSending.value = false;
};
const submit = () => {
    isSending.value = true;

    if (type.value === 'purchase order') {
        return sendPurchaseOrder();
    } else if (type.value === 'invoice') {
        return sendInvoice();
    } else if (type.value === 'KPI Report') {
        return sendKpiReport();
    }
};
const getMailTemplate = () => {
    isLoading.value = true;
    axios
        .get(
            route('api.account.emailTemplate', {
                type: type.value,
                id: emailable.value.id,
            })
        )
        .then(({ data }) => {
            emailContent.value = data.content;
            emailRecipients.value = data.default_recipients;
            availableAttachments.value = data.available_attachments;
            isLoading.value = false;
        });
};
const sendPurchaseOrder = () => {
    axios
        .post(
            route('api.account.event.export.purchaseorders.email', {
                event: emailable.value.event_id,
                purchaseOrder: emailable.value.id,
            }),
            {
                recipients: emailRecipients.value,
                customContent: emailContent.value,
            }
        )
        .then(
            () => {
                capturePosthogEvent('purchase_order_emailed');
                Eventbus.$emit('purchaseOrder:sent', emailable.value.id);
                close();
            },
            () => {
                isSending.value = false;
            }
        );
};
const sendInvoice = () => {
    axios
        .post(
            route('api.account.event.revenue.invoices.emailInvoice', {
                event: emailable.value.event_id,
                revenueEntry: emailable.value.revenue_entry_id,
                customerInvoice: emailable.value.id,
            }),
            {
                emails: emailRecipients.value,
                fileId: selectedAttachment.value,
                customContent: emailContent.value,
            }
        )
        .then(
            () => {
                capturePosthogEvent('customer_invoice_emailed');
                toast(
                    'Invoice Sent',
                    'The invoice ' + emailable.value.invoice_number + ' has been sent to the specified email addresses',
                    'success'
                );
                close();
            },
            () => {
                isSending.value = false;
            }
        );
};
const sendKpiReport = async () => {
    //default_recipients will be empty of kpiReports, get the specified recipients and merge with default_recipients (if any)
    const formData = new FormData();
    emailRecipients.value.forEach((email) => formData.append('emails[]', email));
    if (emailContent.value) {
        formData.append('customContent', emailContent.value);
    }
    if (emailFile.value) {
        formData.append('file', emailFile.value);
    }

    try {
        await axios.post(
            route('api.account.event.kpi-reports.emailKpiReport', {
                event: emailable.value.event_id,
                kpiReport: emailable.value.id,
            }),
            formData
        );
        capturePosthogEvent('kpi_report_emailed');
        Eventbus.$emit('kpiReport:sent', emailable.value.id);
        close();
    } finally {
        isSending.value = false;
    }
};
const addEmailRecipient = () => {
    if (!isValidEmail(emailRecipient.value)) {
        toast('Invalid Email Address', 'Please enter a valid email address and try again', 'error');
        return;
    } else if (emailRecipients.value.includes(emailRecipient.value)) {
        toast('Duplicate Email Address', 'This email address has already been added', 'error');
        return;
    }
    emailRecipients.value.push(emailRecipient.value);
    emailRecipient.value = '';
};
const removeRecipient = (recipient) => {
    emailRecipients.value = emailRecipients.value.filter((email) => email !== recipient);
};
</script>
